import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ConfigConnection from "../../components/Integration/ConfigConnection";
import NavigationBar from "../../components/NavBar/NavBar";
import { useDataContext } from "../../utils/DataContext";
import {
  defaultUiSettings,
  loadUiSettings,
  saveUiSettings,
} from "../../utils/uiSettings";
import { supportedLanguages } from "../../i18n";
import "./SettingPage.css";

function SettingToggle({ checked, label, description, onChange }) {
  return (
    <div className="system-setting-row">
      <div>
        <p className="system-setting-label">{label}</p>
        <p className="system-setting-description">{description}</p>
      </div>
      <button
        type="button"
        className={`system-setting-switch ${checked ? "on" : ""}`}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </div>
  );
}

function SettingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { authReady, userData, userToken } = useDataContext();
  const [savedSettings, setSavedSettings] = useState(loadUiSettings);
  const [settings, setSettings] = useState(savedSettings);

  const hasUnsavedChanges = Object.keys(defaultUiSettings).some(
    (key) => settings[key] !== savedSettings[key]
  );

  useEffect(() => {
    if (authReady && !userToken) navigate("/signin", { replace: true });
  }, [authReady, navigate, userToken]);

  if (!authReady || !userToken) return null;

  const updateSetting = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const save = () => {
    const normalizedSettings = saveUiSettings(settings);
    setSettings(normalizedSettings);
    setSavedSettings(normalizedSettings);
    i18n.changeLanguage(normalizedSettings.language);
  };

  const reset = () => {
    setSettings({ ...defaultUiSettings });
  };

  return (
    <div className="setting-page">
      <NavigationBar />
      <main className="system-settings-shell">
        <header className="system-settings-header">
          <div>
            <p className="system-settings-kicker">{t("settings.workspace")}</p>
            <h1>{t("settings.title")}</h1>
            <p>{t("settings.subtitle")}</p>
          </div>
          {!hasUnsavedChanges && (
            <span className="org-saved-badge">{t("settings.saved")}</span>
          )}
        </header>

        <section className="system-settings-panel" aria-labelledby="appearance-heading">
          <div className="system-settings-section-heading">
            <h2 id="appearance-heading">{t("settings.appearanceTitle")}</h2>
            <p>{t("settings.browserPreference")}</p>
          </div>

          <div className="system-setting-row">
            <div>
              <p className="system-setting-label">{t("settings.language")}</p>
              <p className="system-setting-description">
                {t("settings.languageDescription")}
              </p>
            </div>
            <div
              className="system-setting-segments system-setting-language"
              role="group"
              aria-label={t("settings.language")}
            >
              {supportedLanguages.map(({ code, label }) => (
                <button
                  type="button"
                  key={code}
                  lang={code}
                  className={settings.language === code ? "active" : ""}
                  aria-pressed={settings.language === code}
                  onClick={() => updateSetting("language", code)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="system-setting-row">
            <div>
              <p className="system-setting-label">{t("settings.density")}</p>
              <p className="system-setting-description">
                {t("settings.densityDescription")}
              </p>
            </div>
            <div className="system-setting-segments" aria-label={t("settings.density")}>
              {["comfortable", "compact"].map((density) => (
                <button
                  type="button"
                  key={density}
                  className={settings.density === density ? "active" : ""}
                  aria-pressed={settings.density === density}
                  onClick={() => updateSetting("density", density)}
                >
                  {t(
                    density === "comfortable"
                      ? "settings.comfortable"
                      : "settings.compact"
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="system-setting-row">
            <div>
              <label className="system-setting-label" htmlFor="datasets-per-page">
                {t("settings.datasetsPerPage")}
              </label>
              <p className="system-setting-description">
                {t("settings.datasetsPerPageDescription")}
              </p>
            </div>
            <select
              id="datasets-per-page"
              className="system-setting-select"
              value={settings.datasetsPerPage}
              onChange={(event) =>
                updateSetting("datasetsPerPage", Number(event.target.value))
              }
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={40}>40</option>
            </select>
          </div>

          <SettingToggle
            checked={settings.showIntegratedTools}
            label={t("settings.integratedTools")}
            description={t("settings.integratedToolsDescription")}
            onChange={(value) => updateSetting("showIntegratedTools", value)}
          />
          <SettingToggle
            checked={settings.showDatasetGroups}
            label={t("settings.datasetGroups")}
            description={t("settings.datasetGroupsDescription")}
            onChange={(value) => updateSetting("showDatasetGroups", value)}
          />
          <SettingToggle
            checked={settings.animations}
            label={t("settings.animations")}
            description={t("settings.animationsDescription")}
            onChange={(value) => updateSetting("animations", value)}
          />

          <footer className="system-settings-actions">
            <button
              type="button"
              className="org-btn org-btn-secondary"
              onClick={reset}
            >
              {t("settings.resetDefaults")}
            </button>
            <button
              type="button"
              className="org-btn org-btn-primary"
              onClick={save}
              disabled={!hasUnsavedChanges}
            >
              {t("settings.savePreferences")}
            </button>
          </footer>
        </section>

        {userData?.role === "admin" && (
          <section className="system-settings-admin" aria-labelledby="infrastructure-heading">
            <div className="system-settings-section-heading">
              <h2 id="infrastructure-heading">{t("settings.infrastructure")}</h2>
              <p>{t("settings.infrastructureDescription")}</p>
            </div>
            <ConfigConnection userToken={userToken} />
          </section>
        )}
      </main>
    </div>
  );
}

export default SettingPage;
